/**
 * Centralized API client with authentication + consistent error handling.
 *
 * Goal:
 * - Avoid copy/paste `fetch` boilerplate across pages/services/hooks
 * - Standardize Authorization header injection
 * - Provide consistent error extraction from API responses
 * - Support AbortSignal + timeouts
 */

export interface ApiErrorDetails {
  status: number;
  statusText: string;
  bodyText?: string;
  bodyJson?: any;
}

export class ApiError extends Error {
  constructor(
    message: string,
    public readonly details: ApiErrorDetails
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export interface FetchOptions extends RequestInit {
  /** If provided, adds `Authorization: Bearer <token>` */
  token?: string | null;
  /** If provided, abort the request after this many ms (uses AbortController). */
  timeoutMs?: number;
}

function mergeHeaders(base: HeadersInit | undefined, extra: HeadersInit | undefined): Headers {
  const h = new Headers();
  const apply = (src?: HeadersInit) => {
    if (!src) return;
    if (src instanceof Headers) {
      src.forEach((v, k) => h.set(k, v));
      return;
    }
    if (Array.isArray(src)) {
      for (const [k, v] of src) h.set(k, v);
      return;
    }
    for (const [k, v] of Object.entries(src)) {
      if (typeof v !== 'undefined') h.set(k, String(v));
    }
  };
  apply(base);
  apply(extra);
  return h;
}

async function readBodySafely(res: Response): Promise<{ text: string; json: any | null }> {
  const text = await res.text();
  if (!text) return { text: '', json: null };
  try {
    return { text, json: JSON.parse(text) };
  } catch {
    return { text, json: null };
  }
}

function buildErrorMessage(details: ApiErrorDetails): string {
  const { status, statusText, bodyJson, bodyText } = details;
  if (bodyJson) {
    return String(bodyJson.error || bodyJson.message || `HTTP ${status}: ${statusText}`);
  }
  if (bodyText) return bodyText;
  return `HTTP ${status}: ${statusText}`;
}

/**
 * Make an API request.
 *
 * Notes:
 * - If you pass `data`, it will be JSON-stringified and `Content-Type: application/json` will be set.
 * - If the response is not ok, throws ApiError with parsed body.
 */
export async function apiRequest<T = any>(
  endpoint: string,
  options: FetchOptions & { data?: any } = {}
): Promise<T> {
  const { token, timeoutMs, data, ...fetchOptions } = options;

  // Support timeout without clobbering an existing signal.
  const outerSignal = fetchOptions.signal;
  const controller = timeoutMs ? new AbortController() : null;
  const timeoutId = timeoutMs ? setTimeout(() => controller!.abort(), timeoutMs) : null;

  const signal = (() => {
    if (!controller) return outerSignal;
    if (!outerSignal) return controller.signal;
    // Combine signals (minimal polyfill): if either aborts, abort controller.
    const onAbort = () => controller.abort();
    outerSignal.addEventListener('abort', onAbort, { once: true });
    return controller.signal;
  })();

  const headers = mergeHeaders(
    data !== undefined ? { 'Content-Type': 'application/json' } : undefined,
    fetchOptions.headers
  );

  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }

  try {
    const res = await fetch(endpoint, {
      ...fetchOptions,
      headers,
      body: data !== undefined ? JSON.stringify(data) : fetchOptions.body,
      signal,
    });

    if (!res.ok) {
      const { text, json } = await readBodySafely(res);
      const details: ApiErrorDetails = {
        status: res.status,
        statusText: res.statusText,
        bodyText: text,
        bodyJson: json,
      };
      throw new ApiError(buildErrorMessage(details), details);
    }

    // No content
    if (res.status === 204) return undefined as any;

    const ct = res.headers.get('content-type') || '';
    if (ct.includes('application/json')) {
      return (await res.json()) as T;
    }

    // Best-effort: allow non-JSON responses
    return (await res.text()) as any;
  } catch (err: any) {
    if (err?.name === 'AbortError') {
      throw new Error('Request timed out. Please try again.');
    }
    throw err;
  } finally {
    if (timeoutId) clearTimeout(timeoutId);
  }
}

export async function apiGet<T = any>(endpoint: string, token?: string | null, options: Omit<FetchOptions, 'method' | 'token'> = {}) {
  return apiRequest<T>(endpoint, { ...options, method: 'GET', token });
}

export async function apiPost<T = any>(
  endpoint: string,
  data: any,
  token?: string | null,
  options: Omit<FetchOptions, 'method' | 'token' | 'body'> = {}
): Promise<T> {
  return apiRequest<T>(endpoint, { ...options, method: 'POST', token, data });
}
